/**
 * @file AcmgAnnotationCardContainer.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Mar 23, 2022 16:30
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';

import AcmgAnnotationCard from './AcmgAnnotationCard';

import { arrayExists, objectDeepCopy, objectExists, objectKeyExists, stringExists } from 'src/@common/utilities';
import genericChangeHandler from 'src/@common/genericChangeHandler';
import prepareDiseases from 'src/content/DocumentAnnotator/utilities/prepareDiseases';

import { pubSub, pubSubEvents } from 'src/services/PubSub';

import {
    annotationObjStateUpdate,
    classificationCodeSubmitRequest,
    diseaseSubmitRequest,
    geneSymbolSubmitRequest,
    variantObjDeleteRequest,
    variantObjNewRequest,
    variantObjStateUpdate,
    variantGDotFindRequest,
} from 'src/store/actions/documentAnnotator/documentAnnotatorDb.actions';

function AcmgAnnotationCardContainer(props) {
    const {
        annotationObj,
    } = props;

    const dispatch = useDispatch();

    // start: redux state --------------------------
    const authState = useSelector((state) => {
        return state.auth;
    });

    const { user } = authState;

    const globalState = useSelector((state) => {
        return state.global;
    });

    const { dataLoading } = globalState;

    const documentAnnotatorDbState = useSelector((state) => {
        return state.documentAnnotatorDb;
    });

    const {
        biocurations,
    } = documentAnnotatorDbState;
    // end: redux state -----------------------------

    const [classificationOptions, setClassificationOptions] = useState([]);
    const [diseaseOptions, setDiseaseOptions] = useState([]);
    const [fieldSchema, setFieldSchema] = useState([]);
    const [formFields, setFormFields] = useState({});
    const [formIsDirty, setFormIsDirty] = useState(false);
    const [fieldDirty, setFieldDirty] = useState({});
    const [userCanApprove, setUserCanApprove] = useState(false);

    const [geneSymbolSubmitted, setGeneSymbolSubmitted] = useState(false);

    useEffect(() => {
        // convert fieldSchema to an associative array
        if (objectExists(annotationObj) &&
            objectExists(annotationObj.schemaFields) &&
            arrayExists(annotationObj.schemaFields)) {

            const arr = [];

            annotationObj.schemaFields.forEach((field) => {
                arr[field.fieldName] = field;
            });

            setFieldSchema(arr);

        }

    }, [annotationObj]);

    useEffect(() => {
        // determine if user has approval rights
        if (objectExists(user) &&
            arrayExists(user.roles)) {

            if (user.roles.includes('documentAdmin')) {
                setUserCanApprove(true);
            } else {
                setUserCanApprove(false);
            }

        }

    }, [user]);

    useEffect(() => {
        // classificationOptions
        if (objectExists(annotationObj)) {
            setClassificationOptions(annotationObj.schemaClassifications);
        }

    }, [annotationObj]);

    useEffect(() => {
        // prepare diseaseOptions by using geneModelDiseases to set the 'approved' 
        // property of biocuratedDiseases
        if (objectExists(annotationObj)) {

            let biocuratedDiseases = [];
            if (objectExists(biocurations) &&
                objectKeyExists(biocurations, 'diseases')) {
                biocuratedDiseases = biocurations.diseases;
            }

            const tmpDiseases = prepareDiseases(biocuratedDiseases, annotationObj.geneModelDiseases);
            setDiseaseOptions(tmpDiseases);
        }

    }, [annotationObj, annotationObj.geneModelDiseases, biocurations]);

    useEffect(() => {
        // annotation fields into local state
        // note: using local state because putting fields directly
        //       into redux resulted in very, very slow keystroke updates
        if (objectExists(annotationObj.annotation)) {
            const localObj = objectDeepCopy(annotationObj.annotation);
            setFormFields(localObj);
        }

    }, [annotationObj]);

    // handles changes to main fields
    // and makes a few submissions to the server
    const fieldUpdater = (fieldName, value) => {

        if (fieldName) {

            let tmpFormFields = null;

            switch (fieldName) {

                case 'geneSymbol':
                    tmpFormFields = objectDeepCopy(formFields);
                    tmpFormFields.geneSymbol = value;
                    setFormFields(tmpFormFields);

                    // dispatch to get geneModelDiseases for this geneSymbol
                    dispatch(geneSymbolSubmitRequest(tmpFormFields));
                    setGeneSymbolSubmitted(true);

                    // need to let the submission complete before publishing
                    setTimeout(() => {
                        pubSub.publish(pubSubEvents.AnnotationGeneSymbolSubmitted, tmpFormFields._id);
                    }, 2000);

                    break;

                case 'disease':
                    // dispatch to get classificationCodes approved
                    // for this geneSymbol + disease
                    tmpFormFields = objectDeepCopy(formFields);
                    tmpFormFields.disease = value;
                    setFormFields(tmpFormFields);

                    if (objectExists(tmpFormFields.disease)) {
                        dispatch(diseaseSubmitRequest(tmpFormFields));

                        setTimeout(() => {
                            pubSub.publish(pubSubEvents.AnnotationDiseaseSubmitted, tmpFormFields._id);
                        }, 2000);
                    }

                    break;

                case 'classificationCode':
                    tmpFormFields = objectDeepCopy(formFields);
                    tmpFormFields.classificationCode = value;
                    setFormFields(tmpFormFields);

                    // dispatch classificationCode to get the dynamic fields
                    if (stringExists(value)) {
                        dispatch(classificationCodeSubmitRequest(tmpFormFields));

                        setTimeout(() => {
                            pubSub.publish(pubSubEvents.AnnotationClassificationCodeSubmitted, tmpFormFields._id);
                        }, 2000);
                    }

                    break;

                default:
                    setFormFields({
                        ...formFields,
                        [fieldName]: value
                    });
                    break;
            }

            setFormIsDirty(true);

            setFieldDirty({
                ...fieldDirty,
                [fieldName]: true,
            });
        }
    };

    const handleBlur = () => {
        if (formIsDirty) {
            dispatch(annotationObjStateUpdate(formFields));
            setFormIsDirty(false);
        }
    };

    const handleChange = (event, fieldName) => {
        genericChangeHandler(event, fieldName, fieldUpdater);
    };

    // handles changes to dynamic fields (ie, the annotation.fields object)
    const dynamicFieldUpdater = (fieldName, value) => {

        if (fieldName) {

            // grab the current formFields
            const currentFormFields = Object.assign({}, formFields);

            // grab the current fields object
            const currentFieldsObj = Object.assign({}, formFields.fields);

            // update the current field in the fields obj
            const updatedFieldsObj = {
                ...currentFieldsObj,
                [fieldName]: value,
            };

            // update the formFields with the updated fields object
            const updatedFormFields = {
                ...currentFormFields,
                fields: updatedFieldsObj,
            };

            setFormFields(updatedFormFields);

            setFormIsDirty(true);
            setFieldDirty({
                ...fieldDirty,
                [fieldName]: true,
            });
        }
    };

    const handleDynamicFieldChange = (event, fieldName) => {
        genericChangeHandler(event, fieldName, dynamicFieldUpdater);
    };

    const handleVariantObjAdd = () => {
        // saves the annotation and variantObj to the db
        dispatch(variantObjNewRequest(formFields));
    };

    const handleVariantObjBlur = (variantObj) => {
        // pushes the variantObj back into redux 
        dispatch(variantObjStateUpdate(variantObj, formFields._id));
    };

    const handleVariantObjDelete = (variantObj) => {
        // deletes the variantObj in redux only (not db)
        const annotationId = formFields._id;
        const variantObjId = variantObj._id;
        dispatch(variantObjDeleteRequest(annotationId, variantObjId));
    };

    const handleGDotLookup = (variantId, cDot, transcript) => {
        const annotationId = formFields._id;
        const notify = true;
        dispatch(variantGDotFindRequest(cDot, transcript, annotationId, variantId, notify));
    };

    return (
        <AcmgAnnotationCard
            biocurations={biocurations}
            classificationOptions={classificationOptions}
            dataLoading={dataLoading}
            diseaseOptions={diseaseOptions}
            formFields={formFields}
            fieldSchema={fieldSchema}
            geneSymbolSubmitted={geneSymbolSubmitted}
            userCanApprove={userCanApprove}
            handleBlur={handleBlur}
            handleChange={handleChange}
            handleDynamicFieldChange={handleDynamicFieldChange}
            handleVariantObjAdd={handleVariantObjAdd}
            handleVariantObjBlur={handleVariantObjBlur}
            handleVariantObjDelete={handleVariantObjDelete}
            handleGDotLookup={handleGDotLookup}
        />
    )
}

AcmgAnnotationCardContainer.propTypes = {
    annotationObj: PropTypes.object.isRequired,
};

export default AcmgAnnotationCardContainer;
